import { useMemo } from "react";
import { useT } from "./provider";

/** The one method of Lingui's `I18n` this file uses. Narrowed so a test can build the vocabulary without an i18n instance. */
export interface Translator {
  _(id: string, values?: Record<string, unknown>): string;
}

/**
 * Every word the console draws for a value that comes off the wire.
 *
 * ── Why this file exists at all ─────────────────────────────────────────────
 * The console uses Lingui's runtime API, where **the English source string IS
 * the message id** (`i18n._("Merchants")`). That works everywhere a screen writes
 * a literal, and it does NOT work for a status, a role or a carrier, because
 * those are looked up by a key the api chose: `i18n._(\`status.merchant.${value}\`)`
 * is a template literal, and `lingui extract` cannot see through it. Such a
 * message never reaches the catalog, so `catalogs.test.ts` cannot notice it is
 * untranslated, and the screen renders the raw key.
 *
 * So every one of them is written here as a LITERAL call, in one place, and the
 * lookup happens over the resulting object. The extractor sees all of them, the
 * catalog test covers all of them, and a value the api adds tomorrow fails
 * `status.test.ts` rather than appearing on a register as `IN_FULFILLMENT`.
 *
 * §4.3: sentence case in both languages, including titles. A value rendered from
 * an enum gets its first letter capitalised rather than shown raw.
 */
export function useVocabulary(): Vocabulary {
  const { i18n } = useT();
  return useMemo(() => buildVocabulary(i18n), [i18n]);
}

export type Vocabulary = ReturnType<typeof buildVocabulary>;

export function buildVocabulary(i18n: Translator) {
  {
    const status: Record<string, Record<string, string>> = {
      merchant: {
        draft: i18n._("Draft"),
        submitted: i18n._("Submitted"),
        in_review: i18n._("In review"),
        approved: i18n._("Approved"),
        rejected: i18n._("Rejected"),
        suspended: i18n._("Suspended"),
      },
      kyb: {
        approvable: i18n._("Ready to approve"),
        pending: i18n._("Checks pending"),
        needs_fix: i18n._("Needs fixing"),
        blocked: i18n._("Blocked"),
      },
      kybCheck: {
        pending: i18n._("Pending"),
        passed: i18n._("Passed"),
        failed: i18n._("Failed"),
        // A sanctions HIT is a positive screening result and blocks approval
        // outright; a provider ERROR is us not knowing. Two words, never one.
        hit: i18n._("Sanctions hit"),
        error: i18n._("Provider error"),
      },
      subOrder: {
        PENDING: i18n._("Pending"),
        CONFIRMED: i18n._("Confirmed"),
        PICKING: i18n._("Picking"),
        PACKED: i18n._("Packed"),
        SHIPPED: i18n._("Shipped"),
        DELIVERED: i18n._("Delivered"),
        CANCELLED: i18n._("Cancelled"),
        RETURNED: i18n._("Returned"),
      },
      order: {
        PLACED: i18n._("Placed"),
        AWAITING_PAYMENT: i18n._("Awaiting payment"),
        PAID: i18n._("Paid"),
        IN_FULFILLMENT: i18n._("In fulfilment"),
        COMPLETED: i18n._("Completed"),
        CANCELLED: i18n._("Cancelled"),
      },
      payoutBatch: {
        DRAFT: i18n._("Draft, awaiting release"),
        APPROVED: i18n._("Approved"),
        SENT: i18n._("Sent to the bank"),
        RECONCILED: i18n._("Reconciled"),
        FAILED: i18n._("Failed"),
      },
      payout: {
        PENDING: i18n._("Pending"),
        SENT: i18n._("Sent"),
        RECONCILED: i18n._("Reconciled"),
        FAILED: i18n._("Failed"),
      },
      hold: {
        UNDELIVERED: i18n._("Not delivered yet"),
        DISPUTED: i18n._("Disputed"),
        RETURN_WINDOW: i18n._("Return window open"),
        KYB_PENDING: i18n._("Verification pending"),
        EVENT_PENDING: i18n._("Event not held yet"),
      },
      dispute: {
        OPEN: i18n._("Open"),
        EVIDENCE_REQUIRED: i18n._("Evidence required"),
        UNDER_REVIEW: i18n._("Under review"),
        RESOLVED_BUYER: i18n._("Resolved for the buyer"),
        RESOLVED_MERCHANT: i18n._("Resolved for the merchant"),
        WITHDRAWN: i18n._("Withdrawn"),
      },
      return: {
        REQUESTED: i18n._("Requested"),
        APPROVED: i18n._("Approved"),
        REJECTED: i18n._("Rejected"),
        PICKUP_SCHEDULED: i18n._("Pickup scheduled"),
        RECEIVED: i18n._("Received"),
        INSPECTED: i18n._("Inspected"),
        REFUND_PENDING: i18n._("Refund pending"),
        REFUNDED: i18n._("Refunded"),
        CLOSED: i18n._("Closed"),
        DISPUTED: i18n._("Disputed"),
      },
      issuer: {
        pending: i18n._("Pending"),
        active: i18n._("Active"),
        suspended: i18n._("Suspended"),
      },
      catalog: {
        draft: i18n._("Draft"),
        active: i18n._("Active"),
        archived: i18n._("Archived"),
      },
      unit: {
        RECEIVED: i18n._("Received"),
        ALLOCATED: i18n._("Allocated"),
        PICKING: i18n._("Picking"),
        PACKED: i18n._("Packed"),
        MANIFESTED: i18n._("Manifested"),
        IN_TRANSIT: i18n._("In transit"),
        OUT_FOR_DELIVERY: i18n._("Out for delivery"),
        DELIVERED: i18n._("Delivered"),
        FAILED: i18n._("Attempt failed"),
        RTO: i18n._("Going back to us"),
        RETURNED: i18n._("Returned"),
        CANCELLED: i18n._("Cancelled"),
      },
      codShift: {
        OPEN: i18n._("Open"),
        COUNTING: i18n._("Counting"),
        RECONCILED: i18n._("Reconciled"),
        DISCREPANT: i18n._("Cash does not add up"),
      },
      /**
       * A warehouse wave. `BUILDING` is a wave nobody has released, so no picker
       * is walking for it yet — the distinction between «being assembled» and
       * “released and being worked” is the one a supervisor acts on.
       */
      wmsWave: {
        BUILDING: i18n._("Being built"),
        RELEASED: i18n._("Released"),
        PICKING: i18n._("Being picked"),
        PICKED: i18n._("Picked"),
        CANCELLED: i18n._("Cancelled"),
      },
      /** One operator's task — a pick or a pack. `OPEN` means nobody has taken it. */
      wmsTask: {
        OPEN: i18n._("Waiting for somebody"),
        ASSIGNED: i18n._("Taken"),
        DONE: i18n._("Done"),
        CANCELLED: i18n._("Cancelled"),
      },
      /**
       * A risk case. «Escalated» is not a decision that closes anything — the
       * module leaves the case OPEN behind all three verbs — so the word says
       * who has it next rather than that it is done.
       */
      fraudCase: {
        OPEN: i18n._("Waiting for a decision"),
        APPROVED: i18n._("Cleared"),
        DECLINED: i18n._("Refused"),
        ESCALATED: i18n._("Passed up"),
      },
      /** What the assessment told its caller to do, in the caller's terms. */
      fraudDecision: {
        ALLOW: i18n._("Let through"),
        REVIEW: i18n._("Let through, and flagged"),
        HOLD: i18n._("Held for a person"),
        BLOCK: i18n._("Refused"),
      },
      fraudListing: { BLOCK: i18n._("Blocked"), ALLOW: i18n._("Exempted") },
      ticket: {
        NEW: i18n._("New"),
        OPEN: i18n._("Being answered"),
        PENDING_CUSTOMER: i18n._("Waiting on the customer"),
        RESOLVED: i18n._("Resolved"),
        CLOSED: i18n._("Closed"),
      },
      ticketPriority: {
        LOW: i18n._("Low"),
        MEDIUM: i18n._("Medium"),
        HIGH: i18n._("High"),
        URGENT: i18n._("Urgent"),
      },
      sla: {
        BREACHED: i18n._("Late"),
        DUE_SOON: i18n._("Due within the hour"),
        ON_TRACK: i18n._("On time"),
      },
      feedback: {
        NEW: i18n._("New"),
        ROUTED: i18n._("Opened as a ticket"),
        ACKNOWLEDGED: i18n._("Acknowledged"),
        CLOSED: i18n._("Closed"),
      },
      npsBand: {
        DETRACTOR: i18n._("Detractor"),
        PASSIVE: i18n._("Passive"),
        PROMOTER: i18n._("Promoter"),
      },
      /**
       * «Rejected» never reached a reader; «taken down» was live and was
       * removed, which the author already saw. Two words, never one.
       */
      review: {
        PENDING: i18n._("Waiting for moderation"),
        PUBLISHED: i18n._("Published"),
        REJECTED: i18n._("Rejected"),
        HIDDEN: i18n._("Taken down"),
      },
      affiliatePartner: {
        PENDING: i18n._("Waiting for a decision"),
        ACTIVE: i18n._("Active"),
        // Suspended stops NEW attributions; commissions already earned still
        // settle. The word has to carry that, or it reads as "stopped paying".
        SUSPENDED: i18n._("Suspended, earned commissions still settle"),
        REJECTED: i18n._("Rejected"),
      },
      affiliateCommission: {
        PENDING: i18n._("Inside the return window"),
        APPROVED: i18n._("Owed, awaiting payment"),
        PAID: i18n._("Paid"),
        REJECTED: i18n._("Rejected"),
        CLAWED_BACK: i18n._("Clawed back after a refund"),
      },
      giftCard: {
        ISSUED: i18n._("Minted, not sold"),
        ACTIVE: i18n._("Active"),
        DEPLETED: i18n._("Spent"),
        EXPIRED: i18n._("Expired"),
        VOID: i18n._("Voided"),
      },
      pointLot: {
        PENDING: i18n._("Pending until delivery"),
        CONFIRMED: i18n._("Confirmed"),
        REVERSED: i18n._("Reversed"),
      },
      event: {
        DRAFT: i18n._("Draft"),
        PUBLISHED: i18n._("On sale"),
        SOLD_OUT: i18n._("Sold out"),
        CANCELLED: i18n._("Cancelled"),
        COMPLETED: i18n._("Finished"),
      },
      eventSession: {
        SCHEDULED: i18n._("Scheduled"),
        ON_SALE: i18n._("On sale"),
        CLOSED: i18n._("Sale closed"),
        CANCELLED: i18n._("Cancelled"),
        COMPLETED: i18n._("Finished"),
      },
      /**
       * What happened at the door. Six of the seven are refusals and each says
       * WHICH refusal, because they send a gate steward to three different
       * places: a duplicate is a conversation with the person holding the
       * phone, a wrong session is a door number, and «not open yet» is a clock.
       */
      scanResult: {
        ADMITTED: i18n._("Let in"),
        DUPLICATE: i18n._("Already used"),
        INVALID: i18n._("Not a ticket"),
        WRONG_SESSION: i18n._("Wrong session"),
        VOID: i18n._("Cancelled ticket"),
        NOT_YET_OPEN: i18n._("Doors not open"),
        CLOSED: i18n._("Doors closed"),
      },
    };

    const nav: Record<string, string> = {
      "nav.group.overview": i18n._("Overview"),
      "nav.group.marketplace": i18n._("Marketplace"),
      "nav.group.operations": i18n._("Operations"),
      "nav.group.finance": i18n._("Finance"),
      "nav.group.system": i18n._("System"),
      "nav.dashboard": i18n._("What is waiting"),
      "nav.merchants": i18n._("Merchants"),
      "nav.commission": i18n._("Commission"),
      "nav.catalog": i18n._("Catalogue"),
      "nav.taxonomy": i18n._("Taxonomy"),
      "nav.orders": i18n._("Orders"),
      "nav.disputes": i18n._("Disputes"),
      "nav.dispatch": i18n._("Dispatch"),
      "nav.payables": i18n._("Payables"),
      "nav.payouts": i18n._("Payouts"),
      "nav.trialBalance": i18n._("Trial balance"),
      "nav.tieOuts": i18n._("Tie-outs"),
      "nav.taxIssuers": i18n._("ebarimt issuers"),
      "nav.settings": i18n._("Settings"),
      "nav.group.trust": i18n._("Trust and safety"),
      "nav.group.growth": i18n._("Growth"),
      "nav.analytics": i18n._("Analytics"),
      "nav.risk": i18n._("Risk desk"),
      "nav.riskRules": i18n._("Rules"),
      "nav.riskLists": i18n._("Block and allow lists"),
      "nav.support": i18n._("Support desk"),
      "nav.supportNps": i18n._("Satisfaction"),
      "nav.supportArticles": i18n._("Help centre"),
      "nav.moderation": i18n._("Review moderation"),
      "nav.loyalty": i18n._("Loyalty"),
      "nav.affiliate": i18n._("Affiliate partners"),
      /**
       * «Тасалбартай арга хэмжээ» is 23 characters and clips in a 224px pane at
       * the §5 sizing note's own instruction — measure against the widest
       * MONGOLIAN label, not the English one. Found in a browser, not in review.
       * Shortened rather than the pane widened, because widening it costs every
       * register in the console the same twenty pixels to serve one entry.
       */
      "nav.events": i18n._("Events"),
      "nav.flags": i18n._("Feature flags"),
      "nav.datedSettings": i18n._("Dated settings"),
      "nav.audit": i18n._("Audit register"),
      "nav.system": i18n._("System status"),
    };

    const drillTitle: Record<string, string> = {
      merchant: i18n._("Merchant"),
      subOrder: i18n._("Sub-order"),
      payoutBatch: i18n._("Payout batch"),
      ledgerAccount: i18n._("Account card"),
      product: i18n._("Listing"),
      returnRequest: i18n._("Return request"),
      fraudSubject: i18n._("Risk record"),
      supportTicket: i18n._("Support ticket"),
      loyaltyAccount: i18n._("Loyalty account"),
      ticketedEvent: i18n._("Event"),
    };

    const drillOpen: Record<string, string> = {
      merchant: i18n._("Open the merchant"),
      subOrder: i18n._("Open the sub-order"),
      payoutBatch: i18n._("Open the batch"),
      ledgerAccount: i18n._("Open the account card"),
      product: i18n._("Open the listing"),
      returnRequest: i18n._("Open the return request"),
      fraudSubject: i18n._("Open this subject's risk record"),
      supportTicket: i18n._("Open the ticket"),
      loyaltyAccount: i18n._("Open the loyalty account"),
      ticketedEvent: i18n._("Open the event"),
    };

    const kybCheck: Record<string, string> = {
      dan: i18n._("DAN legal-entity verification"),
      state_registry: i18n._("State registration"),
      bank_account: i18n._("Payout account ownership"),
      sanctions: i18n._("Sanctions screening"),
    };

    const commissionScope: Record<string, string> = {
      default: i18n._("Every sale"),
      merchant: i18n._("One merchant"),
      category: i18n._("One category"),
      tier: i18n._("One tier"),
    };

    const disputeKind: Record<string, string> = {
      CHARGEBACK: i18n._("Chargeback"),
      RETURN_REJECTED: i18n._("Return refused"),
      ITEM_NOT_RECEIVED: i18n._("Item never arrived"),
      REFUND_NOT_RECEIVED: i18n._("Refund never arrived"),
    };

    const returnKind: Record<string, string> = {
      RETURN: i18n._("Return"),
      EXCHANGE: i18n._("Exchange"),
      CANCELLATION: i18n._("Cancellation"),
    };

    const returnReason: Record<string, string> = {
      DAMAGED: i18n._("Arrived damaged"),
      DEFECTIVE: i18n._("Defective"),
      WRONG_ITEM: i18n._("Wrong item"),
      NOT_AS_DESCRIBED: i18n._("Not as described"),
      MISSING_PARTS: i18n._("Parts missing"),
      LATE_DELIVERY: i18n._("Arrived late"),
      CHANGED_MIND: i18n._("Changed their mind"),
      SIZE_OR_FIT: i18n._("Size or fit"),
      BETTER_PRICE_ELSEWHERE: i18n._("Found it cheaper"),
      OTHER: i18n._("Other"),
    };

    /**
     * Why fulfilment handed a unit to the merchant rather than doing it itself.
     * docs/11's rule is that a capability gap is never a SILENT downgrade, so the
     * board draws the reason beside the carrier rather than only «VENDOR».
     */
    const delegationReason: Record<string, string> = {
      NO_CAPABLE_NODE: i18n._("No node can handle it"),
      NO_STOCK_AT_ANY_NODE: i18n._("No node holds the stock"),
      VENDOR_DIRECT_METHOD: i18n._("The merchant ships this themselves"),
      OUT_OF_COVERAGE: i18n._("Outside our coverage"),
    };

    /**
     * A shift's cash outcome. Cash that APPEARED is a finding too — it means a
     * collection was not recorded against the unit it belongs to — so it gets its
     * own word rather than a minus sign in front of the other one.
     */
    const shortfall: Record<string, string> = {
      level: i18n._("Balanced"),
      missing: i18n._("Cash missing"),
      over: i18n._("Cash over"),
    };

    const carrier: Record<string, string> = {
      OWN_FLEET: i18n._("Our own fleet"),
      MONGOL_POST: i18n._("Mongol Post"),
      VENDOR: i18n._("The merchant themselves"),
    };

    /**
     * Why the solver could not place a stop. Never free text upstream and never
     * one word here: each of these is a DIFFERENT next action — «no van has the
     * skill» is a rostering problem, «window unreachable» is a promise problem —
     * and a board that said «unassigned» five times would hide which.
     */
    const unassignedReason: Record<string, string> = {
      NO_VEHICLE_WITH_SKILL: i18n._("No van has the skill for it"),
      CAPACITY_EXCEEDED: i18n._("Bigger than any van"),
      WINDOW_UNREACHABLE: i18n._("Its time window cannot be reached"),
      SHIFT_TOO_SHORT: i18n._("No shift is long enough"),
      NO_CAPACITY_LEFT: i18n._("Every van is full"),
    };

    /**
     * Which travel-time source timed the plan. This is the one word on the board
     * that decides whether a promised time is a road ETA or a straight-line
     * guess, so it is drawn wherever a time is, and never abbreviated.
     */
    const routingEngine: Record<string, string> = {
      valhalla: i18n._("Road network"),
      haversine: i18n._("Straight line"),
    };

    /** The skills a van can carry. The same four service levels, minus PARCEL, which every van has. */
    const fleetSkill: Record<string, string> = {
      BIG_AND_BULKY: i18n._("Big and bulky"),
      SCHEDULED_SLOT: i18n._("Scheduled slot"),
      INSTALL_ASSEMBLY: i18n._("Installation"),
      HAUL_AWAY: i18n._("Haul-away"),
    };

    /** What the warehouse is doing with a batch of orders, and with one operator's task. */
    const wmsHandling: Record<string, string> = {
      PARCEL: i18n._("Parcels"),
      BULKY: i18n._("Bulky"),
      SCHEDULED: i18n._("Scheduled"),
      INSTALL: i18n._("Installation"),
    };

    const glKind: Record<string, string> = {
      asset: i18n._("Asset"),
      liability: i18n._("Liability"),
      equity: i18n._("Equity"),
      income: i18n._("Income"),
      expense: i18n._("Expense"),
    };

    const railFileSide: Record<string, string> = {
      // The three ways a rail's sheet and our ledger can fail to agree. The
      // tolerance is zero, so «differs» is a real answer and not a rounding note.
      FILE_ONLY: i18n._("Only in the bank's file"),
      LEDGER_ONLY: i18n._("Only in our ledger"),
      AMOUNT_DIFFERS: i18n._("The amounts differ"),
    };

    const merchantDecision: Record<string, string> = {
      approve: i18n._("Approve"),
      reject: i18n._("Reject"),
      suspend: i18n._("Suspend"),
      reinstate: i18n._("Reinstate"),
      rerun: i18n._("Re-run verification"),
    };

    /**
     * What a risk record is ABOUT. `POD_DIGEST` is not a party — it is the
     * hash of a proof-of-delivery photo, and «this image has been submitted
     * before» is the cheapest true statement available about driver fraud
     * (docs/17 §4). A register that drew it as «POD_DIGEST» would be asking an
     * ops lead to know that.
     */
    const fraudSubjectKind: Record<string, string> = {
      ACCOUNT: i18n._("Account"),
      DEVICE: i18n._("Device"),
      PHONE: i18n._("Phone number"),
      ADDRESS: i18n._("Delivery address"),
      CARD_BIN: i18n._("Card range"),
      DRIVER: i18n._("Driver"),
      MERCHANT: i18n._("Merchant"),
      POD_DIGEST: i18n._("Delivery photo"),
    };

    /** Where the assessment was asked for — which door the subject came through. */
    const fraudContext: Record<string, string> = {
      SIGNUP: i18n._("Signing up"),
      LOGIN: i18n._("Signing in"),
      CHECKOUT: i18n._("Checkout"),
      PAYMENT: i18n._("Payment"),
      REFUND: i18n._("Refund"),
      DELIVERY: i18n._("Delivery"),
      AFFILIATE: i18n._("Affiliate click"),
      PAYOUT: i18n._("Payout"),
    };

    const fraudSeverity: Record<string, string> = {
      INFO: i18n._("Noted"),
      WARN: i18n._("Worth a look"),
      CRITICAL: i18n._("Serious"),
    };

    /**
     * The nine node kinds a rule expression is built from, in words. The rule
     * editor renders a tree rather than raw JSON for reading, and this is the
     * vocabulary that tree is drawn in.
     */
    const fraudExpressionOp: Record<string, string> = {
      always: i18n._("always matches"),
      fact: i18n._("a fact about the request"),
      present: i18n._("the caller sent"),
      absent: i18n._("the caller did not send"),
      velocity: i18n._("how often, recently"),
      listed: i18n._("is on a list"),
      all: i18n._("all of"),
      any: i18n._("any of"),
      none: i18n._("none of"),
    };

    /** How a fact is compared. Drawn as a phrase so a rule reads as a sentence. */
    const comparator: Record<string, string> = {
      eq: i18n._("is"),
      ne: i18n._("is not"),
      lt: i18n._("is under"),
      lte: i18n._("is at most"),
      gt: i18n._("is over"),
      gte: i18n._("is at least"),
      in: i18n._("is one of"),
      nin: i18n._("is none of"),
      contains: i18n._("contains"),
      startsWith: i18n._("starts with"),
    };

    const ticketSource: Record<string, string> = {
      WEB: i18n._("Website"),
      APP: i18n._("App"),
      EMAIL: i18n._("E-mail"),
      CHAT: i18n._("Chat"),
      PHONE: i18n._("Telephone"),
      API: i18n._("Opened by the system"),
    };

    const ticketCategoryKind: Record<string, string> = {
      ORDER_SUPPORT: i18n._("About an order"),
      GENERAL_SUPPORT: i18n._("General"),
      MERCHANT_SUPPORT: i18n._("From a merchant"),
      DISPUTE: i18n._("Dispute"),
      FEATURE_REQUEST: i18n._("Suggestion"),
      INTERNAL: i18n._("Internal"),
    };

    const messageAuthor: Record<string, string> = {
      CUSTOMER: i18n._("The customer"),
      STAFF: i18n._("Us"),
      MERCHANT: i18n._("The merchant"),
      // Not a person: an assignment, a transition, an SLA breach. Drawn so a
      // reader does not think a colleague wrote «status changed to RESOLVED».
      SYSTEM: i18n._("Recorded automatically"),
    };

    /**
     * The four verbs staff may drive directly. The rest — a customer replying,
     * a ticket auto-closing — are consequences of a message or of time passing
     * and are never offered as buttons.
     */
    const ticketEvent: Record<string, string> = {
      AWAIT_CUSTOMER: i18n._("Wait for the customer"),
      RESOLVED: i18n._("Mark resolved"),
      CLOSED: i18n._("Close"),
      REOPENED: i18n._("Reopen"),
    };

    const slaBreach: Record<string, string> = {
      FIRST_RESPONSE: i18n._("First reply"),
      RESOLUTION: i18n._("Resolution"),
    };

    const moderationReason: Record<string, string> = {
      PROFANITY: i18n._("Bad language"),
      CONTACT_DETAILS: i18n._("Contact details in the text"),
      EXTERNAL_LINK: i18n._("Link to somewhere else"),
      OFF_TOPIC: i18n._("Not about this product"),
      SPAM: i18n._("Spam"),
      ABUSE: i18n._("Abusive"),
      OTHER: i18n._("Other"),
    };

    const moderationDecision: Record<string, string> = {
      PUBLISH: i18n._("Publish"),
      REJECT: i18n._("Reject"),
      HIDE: i18n._("Take down"),
    };

    const reviewTarget: Record<string, string> = {
      PRODUCT: i18n._("A product"),
      MERCHANT: i18n._("A merchant"),
    };

    const affiliateDecision: Record<string, string> = {
      APPROVE: i18n._("Approve"),
      REJECT: i18n._("Reject"),
      SUSPEND: i18n._("Suspend"),
      REINSTATE: i18n._("Reinstate"),
    };

    /**
     * Which click in the attribution window earns. Written out because the
     * difference is the whole commercial argument between two partners, and
     * `LAST_CLICK` on a register tells nobody which one they signed.
     */
    const attributionModel: Record<string, string> = {
      LAST_CLICK: i18n._("The last click before the order"),
      FIRST_CLICK: i18n._("The first click in the window"),
    };

    const payoutMethod: Record<string, string> = {
      WALLET: i18n._("Into their wallet"),
      BANK: i18n._("Bank transfer, through a payout batch"),
    };

    /** Why an operator moved points. Required on every adjustment. */
    const pointAdjustReason: Record<string, string> = {
      GOODWILL: i18n._("Goodwill"),
      SUPPORT_RESOLUTION: i18n._("Settling a support case"),
      MIGRATION: i18n._("Brought over from the old system"),
      FRAUD_CLAWBACK: i18n._("Taken back after fraud"),
      CORRECTION: i18n._("Correcting a mistake"),
      PROMOTION: i18n._("A promotion"),
    };

    const pointMovement: Record<string, string> = {
      EARN_PENDING: i18n._("Earned, pending delivery"),
      EARN_CONFIRMED: i18n._("Earned"),
      EARN_REVERSED: i18n._("Earning reversed"),
      REDEEM: i18n._("Spent"),
      EXPIRE: i18n._("Expired"),
      ADJUST: i18n._("Adjusted by an operator"),
    };

    /** Who paid for a discount or a point grant — us, or the store. */
    const fundingSource: Record<string, string> = {
      PLATFORM: i18n._("Funded by us"),
      MERCHANT: i18n._("Funded by the merchant"),
    };

    const feedbackTarget: Record<string, string> = {
      ORDER: i18n._("An order"),
      MERCHANT: i18n._("A merchant"),
      APP: i18n._("The app itself"),
    };

    const granularity: Record<string, string> = {
      hour: i18n._("By hour"),
      day: i18n._("By day"),
      week: i18n._("By week"),
      month: i18n._("By month"),
    };

    /**
     * How the scanner decided. An OFFLINE scan was allowed on the device and
     * reconciled afterwards, so a duplicate among them is the gate having had no
     * way to know — a different conversation from a duplicate decided online.
     */
    const scanMode: Record<string, string> = {
      ONLINE: i18n._("Checked with us"),
      OFFLINE: i18n._("Decided at the gate"),
    };

    const transferPolicy: Record<string, string> = {
      CLOSED: i18n._("Tickets cannot be handed on"),
      OPEN_UNTIL_START: i18n._("Until the session starts"),
      OPEN_UNTIL_SCAN: i18n._("Until the ticket is scanned"),
    };

    /**
     * The ops queues `GET /v1/admin/analytics/queues` reports. The api answers
     * with a dotted key and a number; the word and the register it links into
     * are the console's, because analytics does not know what a queue is for.
     */
    const queueName: Record<string, string> = {
      "merchants.pending": i18n._("Merchants waiting for approval"),
      "returns.disputes_open": i18n._("Disputes waiting for a decision"),
      "payouts.failed": i18n._("Payouts the bank refused"),
      "tax.receipts_failed": i18n._("ebarimt receipts that failed"),
      "analytics.dead_events": i18n._("Events analytics could not read"),
      "fraud.cases_open": i18n._("Risk cases waiting for a decision"),
      "support.sla_breached": i18n._("Support tickets past their promise"),
      "support.tickets_open": i18n._("Support tickets still open"),
      "reviews.moderation_pending": i18n._("Reviews waiting for moderation"),
    };

    /** Falls back to the raw code rather than throwing: a console that crashed on a value it had not been taught is a console a deploy of the api can take down. */
    const lookup = (table: Record<string, string>, key: string) => table[key] ?? key;

    return {
      status: (domain: string, code: string) => lookup(status[domain] ?? {}, code),
      nav: (id: string) => lookup(nav, id),
      drillTitle: (kind: string) => lookup(drillTitle, kind),
      drillOpen: (kind: string) => lookup(drillOpen, kind),
      kybCheck: (kind: string) => lookup(kybCheck, kind),
      commissionScope: (scope: string) => lookup(commissionScope, scope),
      disputeKind: (kind: string) => lookup(disputeKind, kind),
      returnKind: (kind: string) => lookup(returnKind, kind),
      returnReason: (reason: string) => lookup(returnReason, reason),
      carrier: (code: string) => lookup(carrier, code),
      shortfall: (kind: string) => lookup(shortfall, kind),
      delegationReason: (code: string) => lookup(delegationReason, code),
      unassignedReason: (reason: string) => lookup(unassignedReason, reason),
      routingEngine: (engine: string) => lookup(routingEngine, engine),
      fleetSkill: (skill: string) => lookup(fleetSkill, skill),
      wmsHandling: (handling: string) => lookup(wmsHandling, handling),
      glKind: (kind: string) => lookup(glKind, kind),
      railFileSide: (side: string) => lookup(railFileSide, side),
      merchantDecision: (decision: string) => lookup(merchantDecision, decision),
      fraudSubjectKind: (kind: string) => lookup(fraudSubjectKind, kind),
      fraudContext: (context: string) => lookup(fraudContext, context),
      fraudSeverity: (severity: string) => lookup(fraudSeverity, severity),
      fraudExpressionOp: (op: string) => lookup(fraudExpressionOp, op),
      comparator: (cmp: string) => lookup(comparator, cmp),
      ticketSource: (source: string) => lookup(ticketSource, source),
      ticketCategoryKind: (kind: string) => lookup(ticketCategoryKind, kind),
      messageAuthor: (kind: string) => lookup(messageAuthor, kind),
      ticketEvent: (event: string) => lookup(ticketEvent, event),
      slaBreach: (kind: string) => lookup(slaBreach, kind),
      moderationReason: (reason: string) => lookup(moderationReason, reason),
      moderationDecision: (decision: string) => lookup(moderationDecision, decision),
      reviewTarget: (kind: string) => lookup(reviewTarget, kind),
      affiliateDecision: (decision: string) => lookup(affiliateDecision, decision),
      attributionModel: (model: string) => lookup(attributionModel, model),
      payoutMethod: (method: string) => lookup(payoutMethod, method),
      pointAdjustReason: (reason: string) => lookup(pointAdjustReason, reason),
      pointMovement: (kind: string) => lookup(pointMovement, kind),
      fundingSource: (source: string) => lookup(fundingSource, source),
      feedbackTarget: (target: string) => lookup(feedbackTarget, target),
      granularity: (value: string) => lookup(granularity, value),
      transferPolicy: (policy: string) => lookup(transferPolicy, policy),
      scanMode: (mode: string) => lookup(scanMode, mode),
      /**
       * A queue key the console has not been taught falls through to the raw
       * key rather than being hidden: analytics adds a card when a module
       * lands, and a dashboard that silently dropped it would be a queue nobody
       * is watching. `analytics.test.ts` is what turns that into a build
       * failure instead of a dotted string on a card.
       */
      queueName: (key: string) => lookup(queueName, key),
    };
  }
}
